/**
 * @file: What reaches the screen, moment by moment.
 *
 * A scene shows one playlist at a time. The playlist comes from the interval with
 * the highest priority at that moment. One label for a whole interval is not enough.
 * An interval can be on screen, then off screen, then on screen again, because a
 * higher priority covers the middle stretch. So the code cuts the range at every
 * start and every end. Each piece gets its own winner.
 *
 * The result is in absolute milliseconds. The caller converts a piece to a position
 * on screen. Rounding stays out of this module, so a rounding difference does not
 * become a false seam.
 *
 * The module is shared, because two surfaces need the same result. The first surface
 * is the strip below the week grid. The second is the report of the server on the
 * whole schedule. Two implementations can give different results, and then no result
 * is reliable.
 */

/**
 * A time as milliseconds. The database driver returns a Date for a DATETIME
 * column, and a fixture or the wire gives a string.
 *
 * Exported because every caller that feeds this module has to convert the same
 * way. Three copies of this line existed.
 */
const asMs = value => (value instanceof Date ? value.getTime() : new Date(value).getTime())

/**
 * True when the interval has a priority. The player does not select an interval
 * without one, so that interval never reaches the screen. A null, an undefined value
 * and an empty string all count as no priority.
 *
 * Exported because three places need the same test: the winner calculation here,
 * the report that names what never reaches a screen, and the grid rule that
 * refuses a gesture. Two of them had a copy of it.
 */
const isSchedulable = interval =>
  interval.priority !== null && interval.priority !== undefined && interval.priority !== ''

/**
 * True when two intervals share a moment.
 *
 * Both comparisons are strict, so two intervals laid end to end do not overlap:
 * no moment is in both. segmentsForRange cuts on the same rule, and the server's
 * conflict query uses the same strict comparisons in SQL.
 *
 * @param {Object} one - { start, end } in milliseconds
 * @param {Object} other - { start, end } in milliseconds
 * @returns {boolean}
 */
const overlapsInTime = (one, other) => one.start < other.end && one.end > other.start

/**
 * Give the intervals that hold the highest priority among the ones that cover a
 * stretch. One leader is the winner. Two or more leave the result undefined.
 *
 * @param {Array} bounds - [{ interval, start, end }] that all cover the stretch
 * @returns {Array} The same shape, filtered to the highest priority
 */
function leadersOf (bounds) {
  if (bounds.length === 0) {
    return []
  }

  const highest = Math.max(...bounds.map(bound => Number(bound.interval.priority)))

  return bounds.filter(bound => Number(bound.interval.priority) === highest)
}

/**
 * Cut a range at every interval boundary inside it. Give the leaders of each piece.
 *
 * No piece is merged with its neighbour. Each reader merges on its own rule.
 *
 * @param {Array} intervals - As for segmentsForRange
 * @param {Date|number|string} rangeStart
 * @param {Date|number|string} rangeEnd
 * @returns {Array} [{ start, end, leaders }] in ms and in order. `leaders` holds
 *   the covering intervals with a priority that share the highest priority
 */
function cutRange (intervals, rangeStart, rangeEnd) {
  const from = asMs(rangeStart)
  const to = asMs(rangeEnd)

  if (!(to > from)) {
    return []
  }

  const bounds = (intervals || [])
    .map(interval => ({
      interval,
      start: asMs(interval.start),
      end: asMs(interval.end)
    }))
    .filter(bound =>
      Number.isFinite(bound.start) && Number.isFinite(bound.end) &&
      bound.start < to && bound.end > from)

  const edges = [...new Set(bounds
    .reduce((all, bound) => all.concat([bound.start, bound.end]), [from, to])
    .filter(edge => edge >= from && edge <= to))]
    .sort((a, b) => a - b)

  return edges.slice(0, -1).map((start, index) => {
    const end = edges[index + 1]
    const covering = bounds.filter(bound =>
      bound.start <= start && bound.end >= end && isSchedulable(bound.interval))

    return { start, end, leaders: leadersOf(covering).map(bound => bound.interval) }
  })
}

/**
 * Join each piece to the piece before it when the two touch and `isSame` is
 * true for them. The pieces must be in order.
 *
 * @param {Array} pieces - [{ start, end, ... }]
 * @param {Function} isSame - (last, piece) => boolean
 * @returns {Array} New pieces. The input is not changed
 */
function mergeNeighbours (pieces, isSame) {
  return pieces.reduce((merged, piece) => {
    const lastIndex = merged.length - 1
    const last = merged[lastIndex]

    if (last && last.end === piece.start && isSame(last, piece)) {
      merged[lastIndex] = Object.assign({}, last, { end: piece.end })
    } else {
      merged.push(piece)
    }

    return merged
  }, [])
}

/**
 * Cut a range at every interval boundary inside it. Name the winner of each piece.
 *
 * A piece has no winner in two cases. No interval covers it, or two intervals share
 * the highest priority. A shared highest priority makes the result undefined, so the
 * code selects neither interval. Both cases show as no coverage. That is correct,
 * because the schedule does not define which playlist plays.
 *
 * @param {Array} intervals - { id, start, end, priority }. Dates, ms or strings
 * @param {Date|number|string} rangeStart
 * @param {Date|number|string} rangeEnd
 * @returns {Array} [{ start, end, winner }] in ms and in order. The pieces cover the
 *   whole range. Adjacent pieces with the same winner are merged. `winner` is the
 *   interval or null
 */
function segmentsForRange (intervals, rangeStart, rangeEnd) {
  const pieces = cutRange(intervals, rangeStart, rangeEnd)
    .map(piece => ({
      start: piece.start,
      end: piece.end,
      winner: piece.leaders.length === 1 ? piece.leaders[0] : null
    }))

  return mergeNeighbours(pieces, (last, piece) => last.winner === piece.winner)
}

/**
 * Give each interval that wins a piece of the segments, one time.
 *
 * A piece with no winner adds no interval. The order is the order of the first
 * piece that each interval wins. The server report names the intervals that
 * cover a lost interval. The calendar panel names the intervals that cover a
 * run. Both use this function, so the two lists cannot disagree.
 *
 * @param {Array} segments - From segmentsForRange
 * @returns {Array} The winning intervals
 */
function winnersOf (segments) {
  return [...new Set(segments.map(segment => segment.winner).filter(Boolean))]
}

const sameIntervals = (a, b) => a.length === b.length && a.every(interval => b.includes(interval))

/**
 * Find the stretches where two or more intervals share the highest priority.
 *
 * segmentsForRange cannot give this result. A gap and a tie both have no winner
 * there, so a tie next to a gap becomes one piece with the gap.
 *
 * Two neighbouring pieces become one stretch only when the same intervals are
 * tied in both. A lower interval that starts inside a tie then does not split it.
 *
 * @param {Array} intervals - As for segmentsForRange
 * @param {Date|number|string} rangeStart
 * @param {Date|number|string} rangeEnd
 * @returns {Array} [{ start, end, tied }] in ms and in order. `tied` holds the
 *   intervals that share the highest priority
 */
function tiedStretchesForRange (intervals, rangeStart, rangeEnd) {
  const ties = cutRange(intervals, rangeStart, rangeEnd)
    .filter(piece => piece.leaders.length > 1)
    .map(piece => ({ start: piece.start, end: piece.end, tied: piece.leaders }))

  return mergeNeighbours(ties, (last, piece) => sameIntervals(last.tied, piece.tied))
}

/**
 * Find the intervals that do not reach the screen at any point in the range.
 *
 * `clipped` is true when the interval continues past an edge of the range. For such
 * an interval, the range alone does not show whether it is on screen at another time.
 * A caller that shows a warning must not say "never" for a clipped interval.
 *
 * @param {Array} intervals - As for segmentsForRange
 * @param {Date|number|string} rangeStart
 * @param {Date|number|string} rangeEnd
 * @returns {Array} [{ interval, clipped }]
 */
function neverOnScreen (intervals, rangeStart, rangeEnd) {
  const from = asMs(rangeStart)
  const to = asMs(rangeEnd)
  const winners = new Set(winnersOf(segmentsForRange(intervals, rangeStart, rangeEnd)))

  return (intervals || [])
    .filter(interval => {
      const start = asMs(interval.start)
      const end = asMs(interval.end)
      return Number.isFinite(start) && Number.isFinite(end) &&
        start < to && end > from && !winners.has(interval)
    })
    .map(interval => ({
      interval,
      clipped: asMs(interval.start) < from || asMs(interval.end) > to
    }))
}

/**
 * Give what reaches the screen at one moment.
 *
 * `winner` is the interval the schedule defines. It is null when no interval
 * covers the moment, when no covering interval has a priority, or when two of
 * them hold the highest priority.
 *
 * `tied` holds the intervals that share the highest priority when the schedule
 * defines no winner. It is empty in every other case.
 *
 * The two fields serve two different callers. A caller that reports the schedule
 * shows no coverage for a tie, because the schedule defines nothing there. A
 * caller that must put something on a screen breaks the tie with tieBreak().
 *
 * @param {Array} intervals - As for segmentsForRange
 * @param {Date|number|string} at - The moment
 * @returns {Object} { winner, tied }
 */
function winnerAt (intervals, at) {
  const from = asMs(at)

  if (!Number.isFinite(from)) {
    return { winner: null, tied: [] }
  }

  const covering = (intervals || [])
    .map(interval => ({ interval, start: asMs(interval.start), end: asMs(interval.end) }))
    .filter(bound =>
      isSchedulable(bound.interval) &&
      Number.isFinite(bound.start) && Number.isFinite(bound.end) &&
      bound.start <= from && bound.end > from)

  const leaders = leadersOf(covering)

  if (leaders.length === 1) {
    return { winner: leaders[0].interval, tied: [] }
  }

  return { winner: null, tied: leaders.map(bound => bound.interval) }
}

/**
 * The parts of one interval's own run where it does not reach the screen.
 *
 * A base interval can be on screen, then covered by a takeover, then on screen
 * again. A single mark on the whole bar cannot say that, so the result is one
 * entry for each covered stretch.
 *
 * The values are fractions of the interval's own length. A caller can place them
 * on a bar without knowing how tall the bar is, and without repeating the
 * arithmetic that turns a time into a position.
 *
 * `interval` has to be one of `intervals`. The winner of each piece is compared
 * by identity, so a copy of the row reads as covered for its whole run.
 *
 * @param {Array} intervals - As for segmentsForRange
 * @param {Object} interval - The one to describe, from that same array
 * @returns {Array} [{ from, to }] as fractions, with 0 <= from < to <= 1
 */
function coveredFractionsOf (intervals, interval) {
  const from = asMs(interval && interval.start)
  const to = asMs(interval && interval.end)

  if (!Number.isFinite(from) || !Number.isFinite(to) || !(to > from)) {
    return []
  }

  const span = to - from

  return segmentsForRange(intervals, from, to)
    .filter(piece => piece.winner !== interval)
    .map(piece => ({ from: (piece.start - from) / span, to: (piece.end - from) / span }))
}

/**
 * Break a tie for a caller that must show something.
 *
 * This is a safety net and not a rule of the schedule. Both write paths refuse
 * to create a tie, so a tie means a row from an earlier release. The shortest
 * interval wins, and then the newest row: a short interval is the more specific
 * intent, and a later row is the later decision.
 *
 * The calendar must not call this. It reports what the schedule defines, and a
 * tie there is no coverage. A screen cannot go dark on those grounds, because
 * no coverage already means that another source has the signal.
 *
 * @param {Array} tied - The `tied` field of winnerAt. Each interval needs a
 *   `createdAt` for the second step
 * @returns {Object|null} The interval to show, or null for an empty list
 */
function tieBreak (tied) {
  const age = interval => {
    const at = asMs(interval.createdAt)

    return Number.isFinite(at) ? at : 0
  }

  return [...(tied || [])]
    .sort((one, other) => {
      const lengths = (asMs(one.end) - asMs(one.start)) - (asMs(other.end) - asMs(other.start))

      return lengths !== 0 ? lengths : age(other) - age(one)
    })[0] || null
}

export {
  segmentsForRange,
  winnersOf,
  neverOnScreen,
  asMs,
  isSchedulable,
  overlapsInTime,
  winnerAt,
  tieBreak,
  coveredFractionsOf,
  tiedStretchesForRange
}
